// Stale-while-revalidate snapshots for the landing surfaces.
//
// Why: every hop behind the dashboard measures ~200 ms (portal HTML, the
// API, the SQL — timed 2026-10-15), but they run in SEQUENCE behind the MSAL
// silent token, so the first paint waited on all of them and showed
// "Loading…" for a second or two on every visit. Chauncey lives on the
// dashboard and said it "takes a little longer than I like to load".
//
// The board a person saw last time is a good first paint: the page renders
// it immediately from localStorage, fetches the live payload behind it, and
// swaps. The snapshot is keyed per signed-in account (localAccountId) so one
// browser profile with two sign-ins never shows the other person's "my
// cases". Nothing here is a source of truth — a missing or unparseable
// snapshot just means the old "Loading…" path.

interface Envelope<T> {
  savedAt: string;
  value: T;
}

export function readSnapshot<T>(key: string): { value: T; savedAt: string } | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Envelope<T>;
    if (!parsed || typeof parsed !== 'object' || !('value' in parsed)) return null;
    return { value: parsed.value, savedAt: parsed.savedAt };
  } catch {
    return null;
  }
}

export function writeSnapshot<T>(key: string, value: T): void {
  try {
    const env: Envelope<T> = { savedAt: new Date().toISOString(), value };
    localStorage.setItem(key, JSON.stringify(env));
  } catch {
    // Quota or private mode — the next visit simply loads the slow way.
  }
}
